import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as ProcurementActions from './procurement.actions';
import { ProcurementService } from '../../../../core/services/gse/procurement.service';
import { ToastService } from '../../../../core/services/toast.service';

@Injectable()
export class ProcurementEffects {
  private actions$ = inject(Actions);
  private procurementService = inject(ProcurementService);
  private toastService = inject(ToastService);

  createRequisition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcurementActions.createRequisition),
      switchMap(({ body }) =>
        this.procurementService.createRequisition(body).pipe(
          map((response) =>
            ProcurementActions.createRequisitionSuccess({ response: response.body })
          ),
          catchError((error) =>
            of(
              ProcurementActions.createRequisitionFailure({
                error: error.message || 'Failed to create requisition',
              })
            )
          )
        )
      )
    )
  );

  createRequisitionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProcurementActions.createRequisitionSuccess),
        tap(({ response }) => {
          this.toastService.showSuccess('Success', response.message);
        })
      ),
    { dispatch: false }
  );

  createRequisitionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProcurementActions.createRequisitionFailure),
        tap(({ error }) => {
          this.toastService.showError('Error', error);
        })
      ),
    { dispatch: false }
  );

  createReplenishment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcurementActions.createReplenishment),
      switchMap(({ id, body }) =>
        this.procurementService.createReplenishment(id, body).pipe(
          map((response) =>
            ProcurementActions.createReplenishmentSuccess({ response: response.body })
          ),
          catchError((error) =>
            of(
              ProcurementActions.createReplenishmentFailure({
                error: error.error?.message || error.message || 'Failed to create replenishment',
              })
            )
          )
        )
      )
    )
  );

  createReplenishmentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProcurementActions.createReplenishmentSuccess),
        tap(({ response }) => {
          this.toastService.showSuccess('Success', response.message);
        })
      ),
    { dispatch: false }
  );

  createReplenishmentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProcurementActions.createReplenishmentFailure),
        tap(({ error }) => {
          this.toastService.showError('Error', error);
        })
      ),
    { dispatch: false }
  );

  loadProcurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProcurementActions.loadProcurements),
      switchMap(({ params }) =>
        this.procurementService.getProcurementsResponse(params || {}).pipe(
          map((response) =>
            ProcurementActions.loadProcurementsSuccess({ response: response.body })
          ),
          catchError((error) =>
            of(
              ProcurementActions.loadProcurementsFailure({
                error: error.message || 'Failed to load procurements',
              })
            )
          )
        )
      )
    )
  );

  reloadProcurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProcurementActions.createRequisitionSuccess,
        ProcurementActions.createReplenishmentSuccess
      ),
      map(() =>
        ProcurementActions.loadProcurements({
          params: { page: 0, size: 20, sort: 'id,desc' },
        })
      )
    )
  );
}
